import { Injectable, inject } from '@angular/core';
import { MembershipService } from './membership.service';
import { ContextService } from './context.service';
import { DataService } from './data.service';
import type { Team } from '../models/firestore.types';

type Permission = 'view_all' | 'edit_own' | 'manage_roster' | 'manage_events' | 'manage_settings' | 'edit_all';

/** Normalize odd Firestore values so they still match AppContext.type */
function normalizeEntityType(raw: string | undefined): string {
  const t = (raw || '').toLowerCase().trim();
  const aliases: Record<string, string> = {
    teams: 'team',
    team: 'team',
    organizations: 'organization',
    organisation: 'organization',
    organization: 'organization',
    leagues: 'league',
    league: 'league',
  };
  return aliases[t] || t;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private membershipService = inject(MembershipService);
  private contextService = inject(ContextService);
  private dataService = inject(DataService);

  private rolePermissions: Record<string, Permission[]> = {
    owner: ['view_all', 'edit_own', 'manage_roster', 'manage_events', 'manage_settings', 'edit_all'],
    admin: ['view_all', 'edit_own', 'manage_roster', 'manage_events', 'manage_settings', 'edit_all'],
    manager: ['view_all', 'edit_own', 'manage_roster', 'manage_events'],
    coach: ['view_all', 'edit_own', 'manage_events'],
    player: ['view_all', 'edit_own'],
    parent: ['view_all', 'edit_own'],
    viewer: ['view_all']
  };

  /**
   * All memberships for the active context (same entityType + entityId).
   * Uses relaxed matching (string ids, case-insensitive type) so Firestore quirks
   * like numeric ids or casing do not hide the user's role.
   */
  private membershipsForContext(
    context: { type: string; id: string },
    memberships: { entityType: string; entityId: string; role?: string }[]
  ) {
    const wantType = normalizeEntityType(context.type);
    const wantId = String(context.id ?? '').trim();
    return memberships.filter((m) => {
      const gotType = normalizeEntityType(m.entityType);
      const gotId = String(m.entityId ?? '').trim();
      return gotType === wantType && gotId === wantId;
    });
  }

  private membershipGrantsPermission(
    membership: { role?: string },
    permission: Permission
  ): boolean {
    const roleKey = (membership.role || '').toLowerCase();
    const permissions = this.rolePermissions[roleKey] || [];
    return permissions.includes(permission);
  }

  async hasPermission(permission: Permission): Promise<boolean> {
    const context = this.contextService.currentContext();
    if (!context) return false;

    // Always load from Firestore: the in-memory signal can be stale (e.g. partial
    // list) while length > 0, which previously skipped refresh and denied owner/admin.
    const memberships = await this.membershipService.getUserMemberships();

    const forContext = this.membershipsForContext(context, memberships);
    if (forContext.some((m) => this.membershipGrantsPermission(m, permission))) {
      return true;
    }

    if (context.type === 'team') {
      return await this.permissionGrantedViaParentOrganization(
        context.id,
        permission,
        memberships
      );
    }

    return false;
  }

  /** Org owners/admins can manage teams that belong to their organization. */
  private async permissionGrantedViaParentOrganization(
    teamId: string,
    permission: Permission,
    memberships: { entityType: string; entityId: string; role?: string }[]
  ): Promise<boolean> {
    try {
      const team = await this.dataService.get<Team>('teams', teamId);
      const orgId = team?.organizationId;
      if (!orgId) return false;

      const orgMemberships = this.membershipsForContext(
        { type: 'organization', id: String(orgId) },
        memberships
      );
      return orgMemberships.some((m) =>
        this.membershipGrantsPermission(m, permission)
      );
    } catch {
      return false;
    }
  }

  async canManageRoster(): Promise<boolean> {
    return this.hasPermission('manage_roster');
  }

  async canManageEvents(): Promise<boolean> {
    return this.hasPermission('manage_events');
  }

  async canManageSettings(): Promise<boolean> {
    return this.hasPermission('manage_settings');
  }

  async canEditAll(): Promise<boolean> {
    return this.hasPermission('edit_all');
  }

  async isAdminOrOwner(): Promise<boolean> {
    const context = this.contextService.currentContext();
    if (!context) return false;

    const memberships = await this.membershipService.getUserMemberships();

    const forContext = this.membershipsForContext(context, memberships);
    if (
      forContext.some((m) =>
        ['admin', 'owner'].includes((m.role || '').toLowerCase())
      )
    ) {
      return true;
    }

    if (context.type === 'team') {
      try {
        const team = await this.dataService.get<Team>('teams', context.id);
        const orgId = team?.organizationId;
        if (orgId) {
          const orgMemberships = this.membershipsForContext(
            { type: 'organization', id: String(orgId) },
            memberships
          );
          return orgMemberships.some((m) =>
            ['admin', 'owner'].includes((m.role || '').toLowerCase())
          );
        }
      } catch {
        return false;
      }
    }

    return false;
  }
}
